import {
    cleanupVideoInput,
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    prepareVideoInput,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
};

export const MAX_ROTATE_VIDEO_BYTES = 200 * 1024 * 1024;
export const DEFAULT_TRANSFORM = '90';

export const ROTATE_TRANSFORMS = [
    { id: '270', group: 'rotate' },
    { id: '90', group: 'rotate' },
    { id: '180', group: 'rotate' },
    { id: 'hflip', group: 'flip' },
    { id: 'vflip', group: 'flip' },
];

const FILTER_MAP = {
    90: 'transpose=1',
    180: 'transpose=1,transpose=1',
    270: 'transpose=2',
    hflip: 'hflip',
    vflip: 'vflip',
};

const SUFFIX_MAP = {
    90: 'rotated-90',
    180: 'rotated-180',
    270: 'rotated-270',
    hflip: 'flipped-h',
    vflip: 'flipped-v',
};

export function rotateVideoName(fileName, transform = DEFAULT_TRANSFORM) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    const suffix = SUFFIX_MAP[transform] || transform;
    return `${base}-${suffix}.mp4`;
}

/**
 * @param {File} file
 * @param {string} transform - One of: "90", "180", "270", "hflip", "vflip"
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function rotateVideo(
    file,
    transform = DEFAULT_TRANSFORM,
    onProgress = () => {},
) {
    let ffmpeg;
    let videoInput = null;
    const outputName = 'rotate-output.mp4';

    try {
        ffmpeg = await loadFFmpeg();
        videoInput = await prepareVideoInput(ffmpeg, file);

        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const vf = FILTER_MAP[transform] || FILTER_MAP[DEFAULT_TRANSFORM];

        const exitCode = await ffmpeg.exec([
            '-i', videoInput.inputPath,
            '-vf', vf,
            '-vcodec', 'libx264',
            '-preset', 'ultrafast',
            '-crf', '18',
            '-acodec', 'copy',
            '-movflags', '+faststart',
            outputName,
        ]);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }

        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (error?.message === 'ENCODE_FAILED' || error?.message === 'EMPTY_OUTPUT') {
            throw error;
        }
        throw new Error('ROTATE_FAILED');
    } finally {
        if (ffmpeg && videoInput) {
            await cleanupVideoInput(ffmpeg, videoInput);
        }
    }
}
